import { useEffect, useRef } from 'react';
import {
  BarChart3, BookOpen, CheckCheck, ClipboardCheck, FileText, Gauge, GraduationCap,
  HeartPulse, LayoutDashboard, Leaf, Megaphone, Sparkles, Users,
} from 'lucide-react';
import { SectionHeader } from '../SectionHeader';
import { Reveal } from '../Reveal';
import { trackEvent } from '@/lib/analytics';

const FEATURE_ICONS = {
  'Dashboard': LayoutDashboard,
  'Group View': Gauge,
  'Health Check': HeartPulse,
  'Report an Issue': Megaphone,
  'Reports': FileText,
  'Actions': CheckCheck,
  'AI Assistant': Sparkles,
  'Resource Library': BookOpen,
  'ISO Compliance': ClipboardCheck,
  'Training': GraduationCap,
  'ESG': Leaf,
  'Contractors': Users,
  'Analytics': BarChart3,
};

// Section 4 — core platform capabilities (13 verified modules).
export const ForeseeCapabilities = ({ sp }) => {
  const ref = useRef(null);
  const seen = useRef(false);

  useEffect(() => {
    if (!ref.current || seen.current) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !seen.current) {
          seen.current = true;
          trackEvent('foresee_module_viewed');
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} aria-labelledby="foresee-capabilities-heading" data-testid="foresee-capabilities" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="04"
          eyebrow="Platform Capabilities"
          title="One connected view of EHSQ activity"
          description="Foresee connects frontline activity with management oversight and accountable follow-through — not a standalone incident form or document store."
          testid="foresee-capabilities-header"
        />
        <ul className="mt-14 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
          {(sp.features || []).map((feature, i) => {
            const Icon = FEATURE_ICONS[feature.title] || FileText;
            return (
              <Reveal key={feature.title} delay={(i % 3) * 0.05}>
                <li className="flex items-start gap-4" data-testid={`foresee-feature-${i + 1}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="text-base font-extrabold tracking-tight text-navy-900">{feature.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{feature.text}</p>
                  </div>
                </li>
              </Reveal>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
